import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DollarSign, TrendingUp, Activity, BarChart3, Users, Wallet, ShieldCheck, Zap, Target, Heart } from "lucide-react";
import type { AnyValue } from "@/lib/rowTypes";

interface RewardRule {
  id: string;
  rule_key: string;
  rule_category: string;
  rule_label: string;
  base_amount: number;
  excellence_amount: number;
  perfect_amount: number;
  description: string | null;
  is_active: boolean;
}

interface EconomyHealth {
  total_users: number;
  total_circulation: number;
  avg_checking: number;
  avg_savings: number;
  avg_emergency: number;
  avg_investment: number;
  total_transactions_24h: number;
  total_transactions_7d: number;
  avg_stress: number;
  avg_resilience: number;
  avg_confidence: number;
}

const CATEGORY_META: Record<string, { icon: AnyValue; color: string; label: string }> = {
  lesson: { icon: DollarSign, color: "text-primary", label: "Lesson Rewards" },
  vocabulary: { icon: Target, color: "text-cyan-500", label: "Vocabulary Rewards" },
  checkpoint: { icon: ShieldCheck, color: "text-amber-500", label: "Checkpoint Rewards" },
  capstone: { icon: Zap, color: "text-violet-500", label: "Capstone Rewards" },
  cross_course: { icon: TrendingUp, color: "text-emerald-500", label: "Cross-Course Mastery" },
  module: { icon: BarChart3, color: "text-blue-500", label: "Module Rewards" },
  streak: { icon: Activity, color: "text-orange-500", label: "Streak Rewards" },
  behavior: { icon: Heart, color: "text-pink-500", label: "Behavior Rewards" },
  milestone: { icon: Target, color: "text-teal-500", label: "Allocation Milestones" },
  recovery: { icon: ShieldCheck, color: "text-green-500", label: "Recovery Bonuses" },
};

const RewardRulesPanel = () => {
  const [rules, setRules] = useState<RewardRule[]>([]);
  const [economy, setEconomy] = useState<EconomyHealth | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: rulesData }, { data: econData }] = await Promise.all([
      supabase.from("reward_rules").select("*").order("rule_category").order("rule_key"),
      supabase.rpc("get_economy_health"),
    ]);
    setRules((rulesData) || []);
    if (econData) setEconomy(econData as AnyValue);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const grouped = rules.reduce<Record<string, RewardRule[]>>((acc, r) => {
    (acc[r.rule_category] = acc[r.rule_category] || []).push(r);
    return acc;
  }, {});

  const categories = Object.keys(grouped);

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading reward rules...</div>;

  return (
    <div className="space-y-6">
      <Tabs defaultValue="rules">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="rules">Reward Rules</TabsTrigger>
          <TabsTrigger value="economy">Economy Health</TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="space-y-6 mt-4">
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total Rules" value={rules.length} icon={BarChart3} />
            <StatCard label="Active" value={rules.filter(r => r.is_active).length} icon={Zap} />
            <StatCard label="Categories" value={categories.length} icon={Target} />
            <StatCard label="Max Reward" value={`$${Math.max(...rules.map(r => r.base_amount + (r.perfect_amount || 0)))}`} icon={DollarSign} />
          </div>

          {/* Category groups */}
          {categories.map(cat => {
            const meta = CATEGORY_META[cat] || { icon: DollarSign, color: "text-muted-foreground", label: cat };
            const Icon = meta.icon;
            const items = grouped[cat];
            return (
              <div key={cat} className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                  <h3 className="font-heading font-semibold text-sm text-foreground">{meta.label}</h3>
                  <Badge variant="secondary" className="text-[10px]">{items.length} rules</Badge>
                </div>
                <div className="space-y-1">
                  {items.map(rule => (
                    <div key={rule.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{rule.rule_label}</p>
                        {rule.description && <p className="text-[11px] text-muted-foreground truncate">{rule.description}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm font-bold text-primary">${rule.base_amount}</span>
                        {(rule.excellence_amount || 0) > 0 && (
                          <span className="text-[10px] text-amber-500">+${rule.excellence_amount} exc</span>
                        )}
                        {(rule.perfect_amount || 0) > 0 && (
                          <span className="text-[10px] text-emerald-500">+${rule.perfect_amount} perf</span>
                        )}
                        <Badge variant={rule.is_active ? "default" : "secondary"} className="text-[9px]">
                          {rule.is_active ? "Active" : "Off"}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Pacing guide */}
          <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <h3 className="font-heading font-semibold text-sm text-foreground">Reward Pacing Guide</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
              {[
                { label: "Standard Lesson", range: "$75–90" },
                { label: "Vocab Review", range: "$25–40" },
                { label: "Checkpoint", range: "$125–175" },
                { label: "Capstone", range: "$225–300" },
                { label: "Module", range: "$100–250" },
                { label: "Cross-Course", range: "$325–425" },
              ].map(p => (
                <div key={p.label} className="rounded-lg bg-muted/30 px-3 py-2">
                  <p className="text-muted-foreground">{p.label}</p>
                  <p className="font-bold text-foreground">{p.range}</p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="economy" className="space-y-6 mt-4">
          {economy ? (
            <>
              {/* Macro stats */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <StatCard label="Total Users" value={economy.total_users} icon={Users} />
                <StatCard label="Total Circulation" value={`$${economy.total_circulation.toLocaleString()}`} icon={Wallet} />
                <StatCard label="Txns (24h)" value={economy.total_transactions_24h} icon={Activity} />
                <StatCard label="Txns (7d)" value={economy.total_transactions_7d} icon={BarChart3} />
                <StatCard label="Avg Checking" value={`$${Math.round(economy.avg_checking)}`} icon={DollarSign} />
                <StatCard label="Avg Savings" value={`$${Math.round(economy.avg_savings)}`} icon={DollarSign} />
              </div>

              {/* Account averages */}
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h3 className="font-heading font-semibold text-sm text-foreground">Account Averages</h3>
                <div className="space-y-2">
                  {[
                    { label: "Checking", value: economy.avg_checking, color: "bg-primary" },
                    { label: "Savings", value: economy.avg_savings, color: "bg-blue-500" },
                    { label: "Emergency Fund", value: economy.avg_emergency, color: "bg-amber-500" },
                    { label: "Investment", value: economy.avg_investment, color: "bg-emerald-500" },
                  ].map(a => (
                    <div key={a.label} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-28 shrink-0">{a.label}</span>
                      <div className="flex-1 h-3 bg-muted/30 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${a.color} rounded-full transition-all`}
                          style={{ width: `${Math.min((a.value / Math.max(economy.avg_checking, 1)) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-foreground w-16 text-right">${Math.round(a.value)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Outcome scores */}
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h3 className="font-heading font-semibold text-sm text-foreground">Average Learner Outcomes</h3>
                <div className="grid grid-cols-3 gap-3">
                  <OutcomeCard label="Stress" value={Math.round(economy.avg_stress)} inverted />
                  <OutcomeCard label="Resilience" value={Math.round(economy.avg_resilience)} />
                  <OutcomeCard label="Confidence" value={Math.round(economy.avg_confidence)} />
                </div>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">No economy data available yet.</p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

function StatCard({ label, value, icon: Icon }: { label: string; value: string | number; icon: AnyValue }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3 space-y-1">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-lg font-bold text-foreground">{value}</p>
    </div>
  );
}

function OutcomeCard({ label, value, inverted = false }: { label: string; value: number; inverted?: boolean }) {
  const isGood = inverted ? value < 40 : value > 60;
  const isBad = inverted ? value > 70 : value < 30;
  return (
    <div className={`rounded-xl border p-3 text-center ${
      isGood ? "border-emerald-500/30 bg-emerald-500/5" :
      isBad ? "border-destructive/30 bg-destructive/5" :
      "border-border bg-muted/20"
    }`}>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold ${
        isGood ? "text-emerald-600" : isBad ? "text-destructive" : "text-foreground"
      }`}>{value}</p>
    </div>
  );
}

export default RewardRulesPanel;
